import React from 'react';
import _ from 'lodash';
import type { NextPage } from 'next';
import Head from 'next/head';
import log from '@wix/wix-log';
import { Box, WixDesignSystemProvider } from '@wix/design-system';
import { web } from '@wix/wix-common-aspects';
import { Service } from '@wix/ambassador-category-services/types';
import { Platform } from '@wix/ambassador-wixstudio-templates-v1-template/types';

import TemplateViewer from '@components/TemplateViewer';
import { BiSessionContextProvider } from '@components/Contexts/BiSessionContext';
import { viewListingPath } from '@components/Hooks/useRouter';
import {
  WixNextApiRequest,
  WixNextApiResponse,
  ViewTemplateData,
} from '@common/types';
import TemplatePageHeader from '@components/Template/TemplatePageHeader';
import NotFoundErrorState from '@components/Template/ErrorStates/NotFoundErrorState';
import { HeaderContextProvider } from '@components/Contexts/HeaderContext';
import { CurrencyContextProvider } from '@components/Contexts/CurrencyContext';
import {
  ENABLE_TEMPLATES_CURRENCY_CONVERSION_SPEC,
  TAX_INCLUSIVE_COUNTRIES,
} from '@utils/constants';
import { getWixContext } from '@utils/async-store';

const TemplateViewerPage: NextPage<{
  isMobileUserAgent: boolean;
  viewTemplateData: ViewTemplateData;
  entry: string;
  language: string;
  error?: string;
  host: string;
  isListing: boolean;
  userCurrency: string;
  currencyConversionRate: number;
  taxRate: number;
}> = ({
  isMobileUserAgent,
  viewTemplateData,
  error,
  language,
  entry,
  host,
  isListing,
  userCurrency,
  currencyConversionRate,
  taxRate,
}) => {
  const TemplatePreviewHeader = () => {
    const brand =
      viewTemplateData?.editorType === Platform.STUDIO
        ? 'Wix Studio'
        : 'Wix.com';
    const title = _.isUndefined(viewTemplateData?.title)
      ? `${viewTemplateData?.templateName} | ${brand}`
      : `${viewTemplateData?.title} | Wix Marketplace | ${brand}`;
    return (
      <Head>
        <title>{title}</title>
        <meta name="robots" content="noindex, nofollow" />
        {isMobileUserAgent && viewTemplateData?.editorType === Platform.WIX && (
          <meta name="viewport" content="width=320, user-scalable=yes" />
        )}
      </Head>
    );
  };

  return (
    <>
      <TemplatePreviewHeader />
      <BiSessionContextProvider>
        <WixDesignSystemProvider features={{ newColorsBranding: true }}>
          <CurrencyContextProvider
            currencyData={{
              currencyCode: userCurrency,
              conversionRate: currencyConversionRate,
            }}
            taxRate={taxRate}
          >
            <HeaderContextProvider host={host}>
              {error ? (
                <>
                  <TemplatePageHeader />
                  <Box
                    height={`calc(100vh - ${
                      isMobileUserAgent ? '54px' : '72px'
                    })`}
                  >
                    <NotFoundErrorState />
                  </Box>
                </>
              ) : (
                <TemplateViewer
                  isListing={isListing}
                  entry={entry}
                  isMobileUserAgent={isMobileUserAgent}
                  viewTemplateData={viewTemplateData}
                  language={language}
                />
              )}
            </HeaderContextProvider>
          </CurrencyContextProvider>
        </WixDesignSystemProvider>
      </BiSessionContextProvider>
    </>
  );
};

export async function getServerSideProps({
  req,
  res,
  params,
  query,
}: {
  req: WixNextApiRequest;
  res: WixNextApiResponse;
  params: any;
  query: any;
}) {
  const {
    sourceAndId: [source, id],
  } = params;
  const { entry } = query;
  const wixContext = getWixContext();

  const templateListingAPI = wixContext.serviceFactory.templateListingAPI();
  const templatesAPI = wixContext.serviceFactory.templatesAPI();
  const businessNameAPI = wixContext.serviceFactory.businessNameAPI();
  const metaSiteReadAPI = wixContext.serviceFactory.metaSiteReadAPI();
  const currencyAPI = wixContext.serviceFactory.currencyAPI();
  const taxCalculationAPI = wixContext.serviceFactory.taxCalculationAPI();
  const petriServiceAPI = wixContext.serviceFactory.petriServiceAPI();

  const isCurrencyConversionExperimentEnabled =
    await petriServiceAPI.isExperimentEnabled(
      ENABLE_TEMPLATES_CURRENCY_CONVERSION_SPEC,
    );

  const language = req.aspects?.get(web.Language) || 'en';
  const geo = req.aspects?.get(web.Geo);
  const alpha2CountryCode = geo?.['2lettersCountryCode'];

  try {
    const isListing = source === viewListingPath;

    let templateListing;
    if (isListing) {
      templateListing = await templateListingAPI.getTemplateById(id);
    }
    const publicTemplate = await templatesAPI.getPublicTemplateById(
      isListing ? templateListing.templateId : id,
    );

    if (!publicTemplate) {
      throw Error('publicTemplate not found');
    }
    const { template, templateOwnerAccountId } = publicTemplate;

    const publicMetaSiteId = template?.publicOptions?.publicMetaSiteId;
    const editorType = template?.platform;
    const [templateUrl, businessName] = await Promise.all([
      metaSiteReadAPI.getSiteURL(publicMetaSiteId!),
      businessNameAPI.getBusinessName(templateOwnerAccountId),
    ]);
    const viewTemplateData = {
      businessName,
      templateId: templateListing?.id || template?.id,
      price: templateListing?.price,
      title: templateListing?.title,
      templateName: template?.name,
      templateUrl: templateUrl?.url,
      ownerAccountId: templateOwnerAccountId,
      hasVeloOrBlocks: template?.publicOptions?.includesCode,
      editorType,
      privacyPolicyUrl: templateListing?.privacyPolicyUrl || '',
    };

    const { userCurrency, currencyConversionRate } =
      isCurrencyConversionExperimentEnabled
        ? await currencyAPI.getCurrencyAndConversionRateSupportedByEcom({
            alpha2CountryCode,
          })
        : await currencyAPI.getDefaultCurrencyAndConversionRate();

    // returns taxRate = 0 for non included countries

    let countryTaxRate = 0;
    const countryCode = geo?.['2lettersCountryCode'] || '';
    try {
      if (
        TAX_INCLUSIVE_COUNTRIES.includes(countryCode) &&
        isCurrencyConversionExperimentEnabled
      ) {
        const { taxRate } = await taxCalculationAPI.getTaxRate(
          countryCode,
          userCurrency,
        );
        countryTaxRate = taxRate ?? 0;
      } else {
        countryTaxRate = 0;
      }
    } catch (error: any) {
      log('template viewer')
        .withAspects(req.aspects)
        .info('getCountryTaxRate', {
          message: error.message,
        });
    }

    const props = {
      viewTemplateData: JSON.parse(JSON.stringify(viewTemplateData)),
      language,
      isListing,
      ...(entry && { entry }),
      userCurrency,
      currencyConversionRate,
      taxRate: countryTaxRate,
    };
    return { props };
  } catch (error: any) {
    log('viewTemplate')
      .withAspects(req.aspects)
      .error(`Error: ${error.message}`);
    return {
      props: {
        partnerProfile: {},
        error: error.message,
      },
    };
  }
}

export default TemplateViewerPage;
